import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { BOLT_STAR_ONBOARDING_DIALOG_DISMISSED_KEY } from '@provider-portal/common/LocalStorageKeys';
import { useTargetingParameters } from '@provider-portal/common/experiments/targeting';
import { getStorageItem, setStorageItem } from '@provider-portal/common/utils/storage';
import {
  BOLT_STAR_ROUTE,
  BoltStarAnalyticsEvents,
  isTopPerformerEnabled,
  useTrackBoltStarEvent,
  PopupClosingType,
} from '@provider-portal/mp2-common/topPerformer';

import { BoltStarOnboardingDialog } from './BoltStarOnboardingDialog';

export function BoltStarOnboardingDialogContainer() {
  const history = useHistory();
  const trackEvent = useTrackBoltStarEvent();

  const isBoltStarEnabled = isTopPerformerEnabled(useTargetingParameters());
  // Read once on mount: dismissing during this session should close the dialog with its exit
  // animation, not unmount it mid-transition.
  const [wasDismissedBefore] = useState(
    () => getStorageItem(BOLT_STAR_ONBOARDING_DIALOG_DISMISSED_KEY) === 'true',
  );

  const shouldRender = isBoltStarEnabled && !wasDismissedBefore;
  const [isOpen, setIsOpen] = useState(true);

  const hasTrackedOpen = useRef(false);
  useEffect(() => {
    if (!shouldRender || !isOpen || hasTrackedOpen.current) {
      return;
    }

    hasTrackedOpen.current = true;
    trackEvent(BoltStarAnalyticsEvents.ONBOARDING_POPUP_SHOWN);
  }, [shouldRender, isOpen, trackEvent]);

  const dismiss = () => {
    setStorageItem(BOLT_STAR_ONBOARDING_DIALOG_DISMISSED_KEY, 'true');
    setIsOpen(false);
  };

  const handleClose = () => {
    trackEvent(BoltStarAnalyticsEvents.ONBOARDING_POPUP_CLOSED, {
      closing_type: PopupClosingType.CLOSE_BUTTON,
    });
    dismiss();
  };

  const handleDismiss = () => {
    trackEvent(BoltStarAnalyticsEvents.ONBOARDING_POPUP_CLOSED, {
      closing_type: PopupClosingType.DISMISS_BUTTON,
    });
    dismiss();
  };

  const handleAction = () => {
    trackEvent(BoltStarAnalyticsEvents.ONBOARDING_POPUP_CLOSED, {
      closing_type: PopupClosingType.CALL_TO_ACTION,
    });
    dismiss();
    history.push(BOLT_STAR_ROUTE);
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <BoltStarOnboardingDialog
      isOpen={isOpen}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onAction={handleAction}
    />
  );
}
